import { type ReactNode } from 'react'

import { composeGroupedDropdownReply } from './composeGroupedDropdownReply'
import { ConversationErrorStubStyled } from './styles/ConversationStreamStyled'
import { CatalogItem } from '../../../../components/CatalogItem'
import { ConfirmationOptions } from '../../../../components/ConfirmationOptions'
import {
  GlobalCommandOrgCategoriesForm,
  type IGlobalCommandOrgCategoriesFormPrimaryCategory,
  type IGlobalCommandOrgCategoriesFormValue,
} from '../../../../components/GlobalCommandOrgCategoriesForm'
import { Message } from '../../../../components/Message'
import {
  type ConversationCatalogItemComponent,
  type ConversationChoiceOption,
  type ConversationComponent,
  type ConversationGroupedDropdownComponent,
  type ConversationTurnItem,
} from '../../../../value-objects/ConversationTurnItem'
import {
  ConversationComponentTypes,
  ConversationItemKinds,
} from '../../../../value-objects/enums'

interface RenderConversationItemOptions {
  isStreaming: boolean
  onChoiceSelect: (option: ConversationChoiceOption) => void
  onSendMessage: (message: string) => void
  onCatalogItemSelect: (item: ConversationCatalogItemComponent) => void
}

const isRenderableMessageContent = (content: string): boolean => {
  const trimmed = content.trim()
  return trimmed !== '' && trimmed !== '""'
}

const toPrimaryCategories = (
  component: ConversationGroupedDropdownComponent
): IGlobalCommandOrgCategoriesFormPrimaryCategory[] =>
  component.groups.map((group) => ({
    id: group.id,
    name: group.name,
    secondaryCategories: group.options,
  }))

const isSameId = (a: number | string, b: Nullable<number | string>): boolean =>
  b !== null && String(a) === String(b)

const toInitialValue = (
  component: ConversationGroupedDropdownComponent
): IGlobalCommandOrgCategoriesFormValue | undefined => {
  const group = component.groups.find((candidate) =>
    isSameId(candidate.id, component.defaultGroupId)
  )
  if (!group) return undefined

  const option =
    group.options.find((candidate) =>
      isSameId(candidate.id, component.defaultOptionId)
    ) ?? null

  return {
    primaryCategory: {
      id: group.id,
      name: group.name,
      secondaryCategories: group.options,
    },
    secondaryCategory: option,
  }
}

function renderGroupedDropdown(
  component: ConversationGroupedDropdownComponent,
  key: string,
  disabled: boolean,
  onSendMessage: (message: string) => void
): ReactNode {
  const handleSubmit = (value: IGlobalCommandOrgCategoriesFormValue) => {
    if (!value.primaryCategory || !value.secondaryCategory) return
    onSendMessage(
      composeGroupedDropdownReply({
        groupLabel: component.groupLabel,
        optionLabel: component.optionLabel,
        group: value.primaryCategory,
        option: value.secondaryCategory,
      })
    )
  }

  return (
    <GlobalCommandOrgCategoriesForm
      key={key}
      primaryCategories={toPrimaryCategories(component)}
      initialValue={toInitialValue(component)}
      disabled={disabled}
      onSubmit={handleSubmit}
      onCancel={() => undefined}
    />
  )
}

function renderConversationComponent(
  component: ConversationComponent,
  key: string,
  disabled: boolean,
  onChoiceSelect: (option: ConversationChoiceOption) => void,
  onSendMessage: (message: string) => void,
  onCatalogItemSelect: (item: ConversationCatalogItemComponent) => void
): ReactNode {
  switch (component.type) {
    case ConversationComponentTypes.MESSAGE:
      if (!isRenderableMessageContent(component.content)) return null
      return (
        <Message
          key={key}
          sender={component.sender}
          content={component.content}
        />
      )
    case ConversationComponentTypes.CONFIRMATION_OPTIONS:
      return (
        <ConfirmationOptions
          key={key}
          options={component.options}
          disabled={disabled}
          onSelect={onChoiceSelect}
        />
      )
    case ConversationComponentTypes.GROUPED_DROPDOWN:
      return renderGroupedDropdown(component, key, disabled, onSendMessage)
    case ConversationComponentTypes.CATALOG_ITEM:
      return (
        <CatalogItem
          key={key}
          img={component.img}
          title={component.title}
          body={component.body}
          tags={component.tags}
          price={component.price}
          currency={component.currency}
          onClick={() => onCatalogItemSelect(component)}
        />
      )
    default:
      return null
  }
}

export const renderConversationItem = (
  item: ConversationTurnItem,
  {
    isStreaming,
    onChoiceSelect,
    onSendMessage,
    onCatalogItemSelect,
  }: RenderConversationItemOptions
): ReactNode => {
  switch (item.kind) {
    case ConversationItemKinds.MESSAGE:
      return <Message sender={item.sender} content={item.content} />
    case ConversationItemKinds.ENVELOPE:
      return item.components.map((component, index) =>
        renderConversationComponent(
          component,
          `${item.envelopeId}_${component.type}_${index}`,
          item.isAnswered || isStreaming,
          onChoiceSelect,
          onSendMessage,
          onCatalogItemSelect
        )
      )
    case ConversationItemKinds.ERROR:
      return (
        <ConversationErrorStubStyled role='alert'>
          {item.message}
        </ConversationErrorStubStyled>
      )
    default:
      return null
  }
}
